import "./ClassTab.css";
import React, { useMemo } from "react";
import UiButton from "../../../components/Buttons/UiButton";
import { RpgRules } from "../../../Services/RpgRulesService";
import { IRpgCharacter } from "../../../models/IRpgCharacter";
import { IRpgClass } from "../../../models/IRpgClass";
import ClassSelectionCard from "../../../components/Class/ClassSelectionCard";
import ClassDetailsPanel from "../../../components/Class/ClassDetailsPanel";

interface IClassTabProps {
    isReadOnly: boolean;
    classList: IRpgClass[];
    selectedClass: IRpgClass | null;
    tempCharacter: IRpgCharacter;
    setSelectedClass: (cls: IRpgClass) => void;
    onConfirmClass: (cls: IRpgClass) => void;
}

const buildClassImageSrc = (cls: IRpgClass): string => {
    // Images are optional. If an assets does not exist yet, this returns a path that will 404;
    // components should be built to render nothing on error.
    const safeName = RpgRules.normalizeImageName(cls.Name);
    return `/images/Class/${safeName}.png`;
};

const ClassTab: React.FC<IClassTabProps> = ({
    isReadOnly,
    classList,
    selectedClass,
    tempCharacter,
    setSelectedClass,
    onConfirmClass,
}) => {
    const selectedReqCheck = useMemo(() => {
        if (!selectedClass) {
            return { valid: false, reasons: [] as string[] };
        }

        return RpgRules.checkClassReqs(tempCharacter, selectedClass);
    }, [tempCharacter, selectedClass]);
    const isSelectedLocked = !!selectedClass && !selectedReqCheck.valid;

    const onClass_Select = (cls: IRpgClass) => {
        if (isReadOnly) {
            return;
        }
        setSelectedClass(cls);
    };

    return (
        <div className="class-tab">
            <section className="class-tab__panel">
                <div className="class-tab__panel-header">
                    <h3 className="class-tab__panel-title">Choose your path</h3>
                    <p className="class-tab__panel-subtitle">Select a class to view details</p>
                </div>

                <div className="class-tab__list">
                    {classList.map((cls) => {
                        const isSelected = selectedClass?.Id === cls.Id;
                        const reqCheck = RpgRules.checkClassReqs(tempCharacter, cls);
                        const isLocked = !reqCheck.valid;

                        const summary = isReadOnly ? "View mode active" : isLocked && reqCheck.reasons.length > 0 ? reqCheck.reasons[0] : "";

                        return (
                            <ClassSelectionCard
                                key={cls.Id}
                                rpgClass={cls}
                                isSelected={isSelected}
                                isDisabled={isLocked || isReadOnly}
                                subText={cls.Role}
                                imgSrc={""}
                                disabledReasonSummary={summary}
                                onSelect={onClass_Select}
                            />
                        );
                    })}
                </div>
            </section>

            <section className="class-tab__panel">
                <div className="class-tab__panel-header">
                    <h3 className="class-tab__panel-title">Class details</h3>
                </div>

                <div className="class-tab__details">
                    {selectedClass ? (
                        <>
                            <ClassDetailsPanel
                                rpgClass={selectedClass}
                                imgSrc={buildClassImageSrc(selectedClass)}
                                requirementCheck={selectedReqCheck}
                            />

                            <div className="class-tab__button">
                                <UiButton
                                    variant="sage"
                                    text={`Confirm ${selectedClass.Name}`}
                                    disabled={isReadOnly || isSelectedLocked}
                                    onClickCallback={() => {
                                        if (isReadOnly) { return; }
                                        onConfirmClass(selectedClass);
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="class-tab__details-empty">
                            Select a class to see details.
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default ClassTab;
